"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import {
  User,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  GithubAuthProvider,
  sendEmailVerification,
  signInWithPopup,
  fetchSignInMethodsForEmail,
  sendPasswordResetEmail
} from "firebase/auth";
import { auth, db } from "../../lib/firebase/config";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { UserProfile, SignUpData } from "../../lib/firebase/types";
import { loadReCaptcha, executeReCaptcha } from "../../lib/recaptcha";

const DEFAULT_PROFILE_IMAGE = "/images/avatars/user-1.png";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  userProfile: UserProfile | null;
  signUp: (data: SignUpData) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithGithub: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  logOut: () => Promise<void>;
  getIdToken: () => Promise<string>;
  verifyRecaptcha: (action: string) => Promise<string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  const fetchUserProfile = async (user: User) => {
    try {
      // Reload user to ensure we have latest state
      await user.reload();
      
      const userDoc = await getDoc(doc(db, "users", user.email!));
      if (userDoc.exists()) {
        const profile = userDoc.data() as UserProfile;
        setUserProfile(profile);
        
        // Update verification status if needed
        if (profile.verified !== user.emailVerified) {
          await setDoc(doc(db, "users", user.email!), {
            verified: user.emailVerified
          }, { merge: true });
        }
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };

  useEffect(() => {
    // Store current path before auth changes
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
    if (currentPath !== '/verify-email' && currentPath !== '/login' && currentPath !== '/signup') {
      localStorage.setItem('intendedDestination', currentPath);
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        await fetchUserProfile(user);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    // Initialize reCAPTCHA on client side
    if (typeof window !== 'undefined') {
      loadReCaptcha();
    }

    return () => unsubscribe();
  }, []);

  // Function to execute reCAPTCHA and verify with our server
  const verifyRecaptcha = async (action: string): Promise<string> => {
    try {
      console.log(`Starting reCAPTCHA verification for action: ${action}`);
      
      // For development mode bypass
      if (typeof process !== 'undefined' && 
          process.env.NODE_ENV === 'development' && 
          process.env.NEXT_PUBLIC_BYPASS_RECAPTCHA === 'true') {
        console.warn('DEV MODE: Bypassing reCAPTCHA verification');
        return 'dev-mode-bypass-token';
      }
      
      // Execute reCAPTCHA and get token
      let token;
      try {
        token = await executeReCaptcha(action);
        console.log(`Got reCAPTCHA token, length: ${token.length}`);
      } catch (recaptchaError) {
        console.error('Error executing reCAPTCHA:', recaptchaError);
        // For local dev, allow auth to proceed despite reCAPTCHA issues
        if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
          return 'recaptcha-execution-error-bypass';
        }
        throw new Error('Security verification failed. Please try again.');
      }
      
      // Special token handling - these bypass server verification
      if (token === 'dev-mode-fake-token' || 
          token === 'missing-key-placeholder-token' || 
          token === 'recaptcha-load-failed-token' || 
          token === 'recaptcha-execution-failed-token' ||
          token === 'recaptcha-ready-error-token' ||
          token === 'recaptcha-execute-error-token') {
        console.log(`Using special token: ${token}, bypassing server verification`);
        return token;
      }
      
      // Verify token with our server
      try {
        console.log('Sending token to verification endpoint');
        const response = await fetch('/api/verify-recaptcha', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, action })
        });
        
        if (!response.ok) {
          console.error(`API response not OK: ${response.status}`);
          if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
            return 'api-response-error-bypass';
          }
          throw new Error('Security verification failed. Please try again.');
        }
        
        const result = await response.json();
        console.log('Verification result:', result);
        
        if (!result.success) {
          console.error('Verification failed:', result.message);
          if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
            return 'verification-failed-bypass';
          }
          throw new Error(result.message || 'reCAPTCHA verification failed');
        }
        
        console.log('reCAPTCHA verification successful');
        return token;
      } catch (apiError) {
        console.error('API error during verification:', apiError);
        // For local development, allow auth to proceed
        if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
          return 'api-error-bypass';
        }
        throw new Error('Security verification failed. Please try again.');
      }
    } catch (error) {
      console.error('reCAPTCHA verification error:', error);
      
      // Only show generic error in production
      if (error instanceof Error && typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
        throw error; // Show detailed error in development
      } else {
        throw new Error('Security verification failed. Please try again.');
      }
    }
  };

  const signUp = async (data: SignUpData): Promise<void> => {
    if (!data.password || !data.firstName || !data.lastName) {
      throw new Error("Missing required fields");
    }

    // Verify reCAPTCHA first
    await verifyRecaptcha('signup');

    let userCredential; // Define userCredential outside the try block for cleanup

    try {
      // 1. Check if user is already fully registered in 'users' collection
      const userDocRef = doc(db, "users", data.email);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        throw new Error("This email is already registered. Please sign in instead.");
      }

      // 2. Check if user is in 'pendingUsers' collection
      const pendingUserDocRef = doc(db, "pendingUsers", data.email);
      const pendingUserDocSnap = await getDoc(pendingUserDocRef);

      // Fetch sign-in methods *once*
      const methods = await fetchSignInMethodsForEmail(auth, data.email);

      if (pendingUserDocSnap.exists()) {
        // User is in pendingUsers. Check if it's a password account.
        if (methods.includes('password')) {
          // It's a pending password account. Attempt to get the auth user and resend verification
          try {
            const signInResult = await signInWithEmailAndPassword(auth, data.email, data.password);
            await sendEmailVerification(signInResult.user, {
              url: `${window.location.origin}/verify-email`,
              handleCodeInApp: true,
            });
            throw new Error(`We've resent the verification email to ${data.email}. Please check your inbox and spam folder.`);
          } catch (error: any) {
            if (error.code === 'auth/wrong-password') {
              throw new Error(`An account already exists for ${data.email}. Please check your email for the verification link or try signing in.`);
            }
            throw error;
          }
        }
        // If it's pending but not password (e.g., social), let social check handle it.
      }

      // 3. Check for existing social logins associated with the email
      if (methods.includes('google.com')) {
        throw new Error("This email is already registered with Google. Please sign in with Google.");
      }
      if (methods.includes('github.com')) {
        throw new Error("This email is already registered with GitHub. Please sign in with GitHub.");
      }

      // 4. If no existing user (users, pending password, social), proceed to create Firebase Auth user
      userCredential = await createUserWithEmailAndPassword(
        auth,
        data.email,
        data.password
      );
      const newUser = userCredential.user;

      // 5. Create pending user document in Firestore
      const pendingUser = {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        authMethod: 'manual' as const,
        verified: false,
        createdAt: serverTimestamp()
      };
      await setDoc(pendingUserDocRef, pendingUser); // Use the ref from earlier check

      // 6. Send verification email
      await sendEmailVerification(newUser, {
        url: `${window.location.origin}/verify-email`, // Ensure this matches your verification page route
        handleCodeInApp: true,
      });

      // 7. Redirect (handled by the form component after successful signUp call)
      // No explicit redirect here, let the calling component handle it.
      // window.location.href = '/verify-email'; // Removed redirect from here

    } catch (error: any) {
      console.error("Error signing up:", error);

      // 8. Cleanup: Delete Firebase Auth user if Firestore/email steps failed
      if (userCredential && error.code !== 'auth/email-already-in-use' && !error.message.includes("already started the sign-up process")) {
        // Only delete if we actually created an auth user and the error wasn't a pre-check failure
        try {
          await userCredential.user.delete();
          console.log("Cleaned up orphaned Firebase Auth user:", data.email);
        } catch (deleteError) {
          console.error("Error cleaning up Firebase Auth user:", deleteError);
          // Log this critical failure - might need manual cleanup
        }
      }

      // Re-throw specific known errors or a generic one
      if (error.code === "auth/email-already-in-use") {
        // Firebase Auth reports email is in use. Double-check pendingUsers *now*.
        const pendingUserDocRef = doc(db, "pendingUsers", data.email);
        const pendingUserDocSnap = await getDoc(pendingUserDocRef);
        if (pendingUserDocSnap.exists()) {
          // If it exists in pendingUsers now, it's the verification scenario.
          throw new Error(`You have already started the sign-up process. Please check your email (${data.email}) to verify your account.`);
        } else {
          // If not in pendingUsers, then it's likely fully registered or registered via other means.
          throw new Error("This email address is already registered. Please sign in instead.");
        }
      }
      // Re-throw other errors from our checks or Firebase
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      // Verify reCAPTCHA first
      await verifyRecaptcha('google_login');

      // Block @genwave.xyz emails from using Google login
      const result = await signInWithPopup(auth, new GoogleAuthProvider());
      if (result.user.email?.endsWith("@genwave.xyz")) {
        await signOut(auth);
        throw new Error("Please use manual login for @genwave.xyz accounts");
      }

      // Check if user already exists in Firestore
      const userDocRef = doc(db, "users", result.user.email!);
      const userDocSnap = await getDoc(userDocRef);
      
      let profile: UserProfile;
      
      if (userDocSnap.exists()) {
        // User exists, preserve existing data including API keys
        const existingData = userDocSnap.data() as UserProfile;
        profile = {
          ...existingData,
          uid: result.user.uid,
          displayName: result.user.displayName || existingData.displayName,
          photoURL: result.user.photoURL || existingData.photoURL || DEFAULT_PROFILE_IMAGE,
          authMethod: 'google', // Update auth method
          lastLoginAt: new Date(),
          verified: true, // Google accounts are pre-verified
        };
      } else {
        // Create new profile for first-time users
        profile = {
          uid: result.user.uid,
          email: result.user.email!,
          displayName: result.user.displayName || '',
          photoURL: result.user.photoURL || DEFAULT_PROFILE_IMAGE,
          authMethod: 'google',
          createdAt: new Date(),
          lastLoginAt: new Date(),
          role: 'user',
          subscription: 'Free',
          verified: true, // Google accounts are pre-verified
          apiKeys: [], // Initialize empty API keys array only for new users
        };
      }
      
      // Update or create the user document with merge to preserve fields
      await setDoc(doc(db, "users", result.user.email!), {
        ...profile,
        lastLoginAt: serverTimestamp(),
        // Only set createdAt if it's a new user
        ...(userDocSnap.exists() ? {} : { createdAt: serverTimestamp() })
      }, { merge: true });
      
      setUserProfile(profile);
    } catch (error) {
      console.error("Error signing in with Google:", error);
      throw error;
    }
  };

  const signInWithGithub = async () => {
    try {
      // Verify reCAPTCHA first
      await verifyRecaptcha('github_login');

      // Block @genwave.xyz emails from using GitHub login
      const result = await signInWithPopup(auth, new GithubAuthProvider());
      if (result.user.email?.endsWith("@genwave.xyz")) {
        await signOut(auth);
        throw new Error("Please use manual login for @genwave.xyz accounts");
      }
      
      // Check if user already exists in Firestore
      const userDocRef = doc(db, "users", result.user.email!);
      const userDocSnap = await getDoc(userDocRef);
      
      let profile: UserProfile;
      
      if (userDocSnap.exists()) {
        // User exists, preserve existing data including API keys
        const existingData = userDocSnap.data() as UserProfile;
        profile = {
          ...existingData,
          uid: result.user.uid,
          displayName: result.user.displayName || existingData.displayName,
          photoURL: result.user.photoURL || existingData.photoURL || DEFAULT_PROFILE_IMAGE,
          authMethod: 'github', // Update auth method
          lastLoginAt: new Date(),
          verified: true, // GitHub accounts are pre-verified
        };
      } else {
        // Create new profile for first-time users
        profile = {
          uid: result.user.uid,
          email: result.user.email!,
          displayName: result.user.displayName || '',
          photoURL: result.user.photoURL || DEFAULT_PROFILE_IMAGE,
          authMethod: 'github',
          createdAt: new Date(),
          lastLoginAt: new Date(),
          role: 'user',
          subscription: 'Free',
          verified: true, // GitHub accounts are pre-verified
          apiKeys: [], // Initialize empty API keys array only for new users
        };
      }
      
      // Update or create the user document with merge to preserve fields
      await setDoc(doc(db, "users", result.user.email!), {
        ...profile,
        lastLoginAt: serverTimestamp(),
        // Only set createdAt if it's a new user
        ...(userDocSnap.exists() ? {} : { createdAt: serverTimestamp() })
      }, { merge: true });
      
      setUserProfile(profile);
    } catch (error) {
      console.error("Error signing in with GitHub:", error);
      throw error;
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      // Verify reCAPTCHA first
      await verifyRecaptcha('login');

      // Check if user exists in users collection first
      const userDoc = await getDoc(doc(db, "users", email));
      if (!userDoc.exists()) {
        // Check if in pendingUsers
        const pendingDoc = await getDoc(doc(db, "pendingUsers", email));
        if (pendingDoc.exists()) {
          throw new Error("Please verify your email before logging in.");
        }
        throw new Error("Account not found. Please sign up first.");
      }

      // Check if user is verified
      const userData = userDoc.data() as UserProfile;
      if (!userData.verified) {
        throw new Error("Please verify your email before logging in.");
      }

      // Proceed with login
      try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        console.log("userCredential", userCredential);
        
        // Update last login
        await setDoc(doc(db, "users", email), { 
          lastLoginAt: serverTimestamp() 
        }, { merge: true });
      } catch (authError: any) {
        console.error("Firebase auth error:", authError);
        
        // Handle specific Firebase auth errors separately
        if (authError.code === "auth/wrong-password") {
          throw new Error("Invalid password. Please try again.");
        } else if (authError.code === "auth/user-not-found") {
          // This shouldn't happen since we already checked Firestore
          // But handle it consistently anyway
          throw new Error("Invalid email or password.");
        } else if (authError.code === "auth/too-many-requests") {
          throw new Error("Too many failed attempts. Please try again later.");
        } else {
          throw new Error("Failed to sign in. Please check your credentials.");
        }
      }
    } catch (error: any) {
      console.error("Error signing in:", error);
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      // Verify reCAPTCHA first
      await verifyRecaptcha('reset_password');

      console.log("resetPassword called for email:", email);
      
      // Normalize email to lowercase
      const normalizedEmail = email.toLowerCase();
      
      // First check if the email exists in our system
      const methods = await fetchSignInMethodsForEmail(auth, normalizedEmail);
      console.log("Sign-in methods for this email:", methods);
      
      // Also check Firestore to ensure we don't miss users that might have auth issues
      const userDoc = await getDoc(doc(db, "users", normalizedEmail));
      const userExists = userDoc.exists();
      
      console.log("User exists in Firestore:", userExists);
      
      // Only proceed with password reset for email/password accounts or if user exists in Firestore
      if (methods.includes('password') || userExists) {
        console.log("Account exists, proceeding with password reset");
        try {
          await sendPasswordResetEmail(auth, normalizedEmail, {
            // Add URL to redirect back to application after password reset
            url: `${window.location.origin}/login`,
            handleCodeInApp: true
          });
          console.log("Password reset email sent successfully");
        } catch (innerError: any) {
          console.error("Firebase sendPasswordResetEmail error:", innerError);
          
          // If user exists in Firestore but not in Auth, we need to handle specially
          if (userExists && innerError.code === 'auth/user-not-found') {
            console.error("User exists in Firestore but not in Auth - potential data inconsistency");
            // Still show success to user for security, but log the error for investigation
          } else {
            throw innerError;
          }
        }
      } else if (methods.length > 0) {
        // If user exists but doesn't use password auth (e.g., uses social login)
        console.log("Account exists but uses social login, not sending password reset");
        throw new Error("This account uses social login. Please sign in with Google or GitHub.");
      } else {
        // We don't want to reveal if an email exists or not for security reasons,
        // so we'll show the same success message even if the email doesn't exist
        console.log("No account found for this email, but not revealing this to user");
      }
    } catch (error: any) {
      console.error("Error in resetPassword function:", error);
      // Don't expose detailed errors to users for security reasons
      if (error.message?.includes("social login")) {
        throw error; // Only pass through our custom social login error
      }
      throw new Error("Failed to send password reset email. Please try again later.");
    }
  };

  const logOut = async () => {
    try {
      await signOut(auth);
      setUserProfile(null);
    } catch (error) {
      console.error("Error signing out:", error);
      throw error;
    }
  };

  // Function to get the current user's ID token
  const getIdToken = async () => {
    if (!user) {
      throw new Error('No user logged in');
    }
    try {
      return await user.getIdToken();
    } catch (error) {
      console.error('Error getting ID token:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        userProfile,
        signUp,
        signIn,
        signInWithGoogle,
        signInWithGithub,
        resetPassword,
        logOut,
        getIdToken,
        verifyRecaptcha,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
